import React from "react";

/**
 * Renders "PO # ▲▼" as a clickable inline header cell content. Meant to be
 * placed INSIDE the existing <th>PO #</th> cells so each table keeps its own
 * color / padding scheme untouched.
 *
 * Props:
 *   sort         – "asc" | "desc" | null
 *   onChange(dir) – callback with the new direction. Clicking cycles asc→desc→asc.
 *   testid       – optional prefix for automation hooks (default "po-sort").
 */
export function PoSortHeader({ sort, onChange, testid = "po-sort" }) {
  const nextDir = sort === "asc" ? "desc" : "asc";
  return (
    <button
      type="button"
      onClick={() => onChange(nextDir)}
      className="inline-flex items-center gap-1 hover:opacity-80 focus:outline-none select-none uppercase tracking-wider"
      title={`Sort by PO # · currently ${sort || "unsorted"} · click for ${nextDir}`}
      data-testid={`${testid}-toggle`}
    >
      <span>PO #</span>
      <span className="inline-flex flex-col items-center leading-none ml-0.5" aria-hidden="true">
        <span
          data-testid={`${testid}-asc`}
          className={sort === "asc" ? "opacity-100" : "opacity-30"}
          style={{ fontSize: "8px", lineHeight: "8px" }}
        >
          ▲
        </span>
        <span
          data-testid={`${testid}-desc`}
          className={sort === "desc" ? "opacity-100" : "opacity-30"}
          style={{ fontSize: "8px", lineHeight: "8px" }}
        >
          ▼
        </span>
      </span>
    </button>
  );
}

/**
 * Sort a list by `po_number`. Uses numeric compare when both values look
 * numeric ("4744", "3900"), otherwise natural string compare. Empty PO
 * numbers always sink to the bottom regardless of direction.
 * Returns a new array. Returns the input untouched when `dir` is falsy.
 */
export function sortByPo(list, dir) {
  if (!dir || !Array.isArray(list) || list.length < 2) return list;
  const asc = dir === "asc";
  const raw = (o) => (o?.po_number ?? "").toString().trim();
  const numeric = (v) => {
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && /^-?\d+(\.\d+)?$/.test(v) ? n : null;
  };
  return [...list].sort((a, b) => {
    const av = raw(a);
    const bv = raw(b);
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    const an = numeric(av);
    const bn = numeric(bv);
    if (an !== null && bn !== null) return asc ? an - bn : bn - an;
    return asc
      ? av.localeCompare(bv, undefined, { numeric: true })
      : bv.localeCompare(av, undefined, { numeric: true });
  });
}
